import * as LDClient from 'launchdarkly-js-client-sdk';
import config from './../../config';
import { isInternalStaff } from '../../utils/user';
import { ENV } from './../../environment';
const CONFIG = config[ENV];
import {
  ADVANCED_READONLY_USER_ROLE,
  ADVANCED_USER_ROLE,
  ADMIN_ROLE,
} from '@/constants/userRoles';

export const isUploadAccessBlocked = (featureFlags, currentUser) => {
  return Boolean(
    featureFlags.block_upload_access && !isInternalStaff(currentUser),
  );
};

export const canDownloadBusinessComplianceCSV = (featureFlags, currentUser) => {
  return Boolean(
    featureFlags.view_business_compliance_agent && isInternalStaff(currentUser),
  );
};

export default {
  state: {
    featureFlags: [],
  },
  mutations: {
    setFeatureFlags(state, user) {
      if (!user) {
        return;
      }

      const context = {
        key: user.id.toString(),
        kind: 'user',
        organisation_name: user.organisation.name,
      };
      const client = LDClient.initialize(CONFIG.launch_darkly.key, context, {
        hash: user.launch_darkly_hash,
      });
      client.on('ready', () => {
        state.featureFlags = client.allFlags();
      });
    },
  },
  getters: {
    isIntercomChatEnabled: (state) =>
      state.featureFlags.is_intercom_chat_enabled,
    documentStatusEnabled: (state) =>
      state.featureFlags['create-document-review-status'],
    documentFilteringEnabled: (state) =>
      state.featureFlags['document-review-status-filtering'],
    canUseCredit: (state) =>
      state.featureFlags.is_credit_analysis_endpoint_enabled,
    creditConfidenceScoreThreshold: (state) => {
      const fallbackConfidenceScore = 0.5;
      return (
        parseFloat(state.featureFlags.credit_confidence_score_threshold) ||
        fallbackConfidenceScore
      );
    },
    creditTransactionsSearchFiltersEnabled: (state) =>
      state.featureFlags.credit_transactions_search_filters,
    creditBulkRecategorisationEnabled: (state) =>
      state.featureFlags.credit_bulk_recategorisation_enabled,
    creditInsightsTabPreviewEnabled: (state) =>
      state.featureFlags.credit_insights_tab_preview,
    creditInsightsTabsEnabled: (state, getters) =>
      getters.canUseCredit || getters.creditInsightsTabPreviewEnabled,
    creditDisableTrustScoreAlert: (state) =>
      state.featureFlags.credit_disable_trust_score_alert,
    creditMonthlyTableViewEnabled: (state) =>
      state.featureFlags.credit_monthly_table_view,
    creditReconciledDocsFilterEnabled: (state) =>
      state.featureFlags.credit_reconciled_docs_filter_enabled,
    creditReconciledDocsFilterDefaultValue: (state) =>
      state.featureFlags.credit_reconciled_docs_filter_default_value,
    creditAgentEnabled: (state) => state.featureFlags.enable_credit_agent,
    collectEnabled: (state) =>
      state.featureFlags['new-collect-session-button-enabled'],
    parsedDetailHighlighterEnabled: (state) =>
      state.featureFlags['parsed-detail-highlighter'],
    isCollectLiveCaptureEnabled: (state) =>
      state.featureFlags.collect_live_capture_mode,
    isWorkspaceCreationEnabled: (state) =>
      state.featureFlags.is_workspace_creation_enabled,

    // this flag controls access to the user management API, the access grant API,
    // and the basic readonly user role (because this role can't do anything without access grants)
    canAccessUserManagementApi: (state) =>
      state.featureFlags.can_access_user_management_api,

    isUploadAccessBlocked: (state, _, rootState) =>
      isUploadAccessBlocked(state.featureFlags, rootState.currentUser),
    isUserFeedbackEnabled: (state) =>
      state.featureFlags.is_user_feedback_enabled,
    useOcrForTruPdfCalculations: (state) =>
      state.featureFlags['use-ocr-for-document-calculations'],
    includeLegacyAgentOutputInApiResponse: (state) =>
      state.featureFlags.include_legacy_agent_output_in_api_response,
    canViewBusinessComplianceAgent: (state) =>
      state.featureFlags.view_business_compliance_agent,
    canViewCustomerFraudReasoningAgent: (state) =>
      state.featureFlags.view_customer_fraud_reasoning_agent_in_ui,
    hideCreditWhenLowConfidenceScore: (state) =>
      state.featureFlags.hide_credit_low_confidence_score,
    showTransactionsSignals: (state) =>
      state.featureFlags.serialize_transaction_signals,
    canDownloadBusinessComplianceCSV: (state, _, rootState) =>
      canDownloadBusinessComplianceCSV(
        state.featureFlags,
        rootState.currentUser,
      ),
    processingJobsEnabled: (state) => state.featureFlags.enable_processing_jobs,
    isCustomerFraudReportEnabled: (state) =>
      state.featureFlags.enable_customer_fraud_report,
    unifiedAgentEnabled: (state) => state.featureFlags['unified-agent-enabled'],

    // computed flags
    isChatAgentEnabled: (state, _, rootState) => {
      // We only allow advanced users and admins to use the Chat Agent via the
      // backend API, so we only consider this flag "enabled" if flag is on AND
      // the user is advanced or admin.
      if (!rootState.currentUser?.role) {
        return false;
      }
      const isAdvancedUserOrAdmin = [
        ADVANCED_USER_ROLE,
        ADVANCED_READONLY_USER_ROLE,
        ADMIN_ROLE,
      ].includes(rootState.currentUser.role);
      return (
        state.featureFlags.enable_chat_agent_frontend && isAdvancedUserOrAdmin
      );
    },
    userShouldSeeTrustScore: (state, _, rootState) => {
      return (
        !rootState.currentUser?.organisation?.web_search_enabled &&
        !state.featureFlags.view_business_compliance_agent
      );
    },
  },
};
